import { defineStore } from 'pinia';
import { getPlatform } from '@utils/methods';
import { SYSTEM_INFO, USER_INFO } from '@config/constant';
import { assignObj } from '@utils/methods';
import { ref, computed } from 'vue';

// 定义常量
const ACCOUNTS_KEY = 'MULTI_ACCOUNTS';

// 账户信息接口
interface AccountInfo {
	token: string;
	userInfo: any;
	timestamp: number;
	isCurrent: boolean;
}

export default defineStore('systemInfo', () => {
	// 状态
	const sh = ref(0);
	const bh = ref(0);
	const nh = ref(0);
	const th = ref(0);
	const token = ref('');
	const token2 = ref('');
	const plat = ref(getPlatform());
	const locationCache = { lng: '' as string | number, lat: '' as string | number }; // 定位结果缓存（会话级别）
	const menuData = ref({
		top: '',
		left: '',
		right: '',
		width: '',
		height: '',
		bottom: '',
		rightM: '',
	});

	// 账户列表 - 使用ref实现响应式
	const accountsList = ref<AccountInfo[]>([]);

	// 初始化账户列表
	function initAccountsList() {
		try {
			const savedAccounts = uni.getStorageSync(ACCOUNTS_KEY);
			if (savedAccounts) {
				let accounts = JSON.parse(savedAccounts);

				// 清理无效账户数据
				accounts = cleanInvalidAccounts(accounts);

				accountsList.value = accounts;
			}
		} catch (error) {
			console.error('初始化账户列表失败', error);
			accountsList.value = [];
		}
	}

	// 清理无效账户数据
	function cleanInvalidAccounts(accounts: AccountInfo[]) {
		if (!Array.isArray(accounts)) return [];

		// 过滤出有效账户
		const validAccounts = accounts.filter((acc) => {
			// 检查userInfo是否是有效的用户数据
			return acc && acc.userInfo && acc.userInfo.code === 0 && acc.userInfo.mid;
		});

		// 如果有无效账户被移除，保存更新后的列表
		if (validAccounts.length !== accounts.length) {
			console.log('清理了无效账户数据', accounts.length - validAccounts.length);
			uni.setStorageSync(ACCOUNTS_KEY, JSON.stringify(validAccounts));
		}

		return validAccounts;
	}

	// 是否显示环境切换
	const showSwitch = computed(() => {
		switch (plat.value) {
			case 'ios':
			case 'android':
				const version = uni.getSystemInfoSync().appWgtVersion;
				return version?.indexOf('test') != -1;
			case 'mp':
				return UNI_ENV == 'dev';
			default:
				return false;
		}
	});

	// 判断用户是否已登录
	const isLoggedIn = computed(() => !!token.value);

	// 获取保存的账户列表
	function getAccountsList(): AccountInfo[] {
		return accountsList.value;
	}

	// 保存账户列表到本地存储
	function saveAccountsList(accounts: AccountInfo[]) {
		accountsList.value = accounts;
		uni.setStorageSync(ACCOUNTS_KEY, JSON.stringify(accounts));
	}

	// 更新当前账户信息
	function updateCurrentAccount(userToken: string, userInfo: any) {
		// 验证token和userInfo
		if (!userToken || !userInfo || Object.keys(userInfo).length === 0) return;

		// 检查userInfo是否是有效的用户数据
		// 只有code为0且包含mid字段的数据才是有效的用户数据
		if (userInfo.code !== 0 || !userInfo.mid) {
			console.log('无效的用户数据，不保存账号信息', userInfo);
			return;
		}

		const accounts = [...accountsList.value]; // 创建副本以确保响应式更新
		const existingIndex = accounts.findIndex((acc) => acc.userInfo.mid === userInfo.mid);

		// 更新所有账户为非当前
		accounts.forEach((acc) => {
			acc.isCurrent = false;
		});

		if (existingIndex >= 0) {
			// 更新已存在的账户
			accounts[existingIndex] = {
				token: userToken,
				userInfo,
				timestamp: Date.now(),
				isCurrent: true,
			};
		} else {
			// 添加新账户
			accounts.push({
				token: userToken,
				userInfo,
				timestamp: Date.now(),
				isCurrent: true,
			});
		}

		saveAccountsList(accounts);
	}

	// 设置token
	function setToken(newToken: string = '') {
		if (newToken === token.value) return;

		token.value = newToken;
		projectData().getShareData();
		projectData().fetchConfig('init');
		projectData()
			.fetchUserInfo('init')
			.then((userInfo) => {
				// 如果有用户信息，更新当前账户
				if (newToken && userInfo && Object.keys(userInfo).length > 0) {
					updateCurrentAccount(newToken, userInfo);
				}
			});
		useReferralBindingStore().bindReferral();
	}

	// 初始化小程序菜单信息
	function mpInit() {
		// #ifdef MP
		const menuInfo: any = uni.getMenuButtonBoundingClientRect();
		const { screenWidth } = uni.getSystemInfoSync();
		menuInfo.rightM = screenWidth - menuInfo.left;
		menuData.value = menuInfo;
		// #endif
	}

	// 设置系统数据
	function setSysData(query: any) {
		const _sysInfo = uni.getStorageSync(SYSTEM_INFO);

		const { lat, lng } = query;
		if (lat && lng) {
			useLocationStore().regeoLocation(lat, lng, true);
		}

		const state = {
			sh: sh.value,
			bh: bh.value,
			nh: nh.value,
			th: th.value,
			token: token.value,
			token2: token2.value,
			plat: plat.value,
			menuData: menuData.value,
		};

		let updatedState = _sysInfo ? assignObj(state, _sysInfo) : state;
		updatedState = assignObj(updatedState, query);

		// 更新各个响应式状态
		sh.value = updatedState.sh;
		bh.value = updatedState.bh;
		nh.value = updatedState.nh;
		th.value = updatedState.th;
		token2.value = updatedState.token2;
		plat.value = updatedState.plat;
		menuData.value = updatedState.menuData;

		// 单独处理token，因为它会触发其他操作
		setToken(updatedState.token);

		mpInit();

		// 初始化账户列表
		initAccountsList();
	}

	// 切换当前账户
	function switchToAccount(accountId: string | number) {
		const accounts = [...accountsList.value]; // 创建副本以确保响应式更新
		const account = accounts.find((acc) => acc.userInfo.mid == accountId);
		if (!account) return false;

		// 更新所有账户为非当前
		accounts.forEach((acc) => {
			acc.isCurrent = acc.userInfo.mid == accountId;
		});

		// 更新系统信息和用户登录状态
		saveAccountsList(accounts);
		setToken(account.token);
		uni.setStorageSync(USER_INFO, account.userInfo);

		return true;
	}

	// 添加方法来处理准备添加新账号的场景
	function switchToNewAccount() {
		// 先保存当前账号信息到账号列表
		if (token.value && isLoggedIn.value) {
			const accounts = [...accountsList.value]; // 创建副本以确保响应式更新
			const userInfo = uni.getStorageSync(USER_INFO);

			if (userInfo && Object.keys(userInfo).length > 0) {
				// 检查账号是否已存在
				const existingIndex = accounts.findIndex((acc) => acc.userInfo.mid == userInfo.mid);

				if (existingIndex >= 0) {
					// 更新已存在的账号
					accounts[existingIndex].token = token.value;
					accounts[existingIndex].userInfo = userInfo;
					accounts[existingIndex].timestamp = Date.now();
					accounts[existingIndex].isCurrent = true;
				} else if (userInfo.mid) {
					// 添加新账号
					accounts.push({
						token: token.value,
						userInfo,
						timestamp: Date.now(),
						isCurrent: true,
					});
				}

				saveAccountsList(accounts);
				console.log('保存账户列表成功：', accounts);
			}
		}

		// 清除当前token，准备登录新账号
		// token.value = '';
		// uni.removeStorageSync(USER_INFO);
	}

	// 清理并刷新账户列表
	function refreshAccountsList() {
		const accounts = getAccountsList();
		const validAccounts = cleanInvalidAccounts([...accounts]);
		accountsList.value = validAccounts;
		return validAccounts;
	}

	// 设置指定账户为当前账户
	function setAccountCurrent(accountId: string | number) {
		const accounts = [...accountsList.value]; // 创建副本以确保响应式更新

		// 更新所有账户状态
		const updatedAccounts = accounts.map((acc) => {
			acc.isCurrent = acc.userInfo.mid == accountId;
			return acc;
		});

		saveAccountsList(updatedAccounts);
		return updatedAccounts.find((acc) => acc.isCurrent);
	}

	// 自动登录第一个账户
	function autoLoginFirstAccount() {
		// 如果已有token，不需要自动登录
		if (token.value) return false;

		// 获取并清理账户列表
		const accounts = refreshAccountsList();

		// 没有账户则不处理
		if (!accounts || accounts.length === 0) return false;

		// 找出第一个有效账户
		const firstAccount = accounts[0];

		if (firstAccount && firstAccount.token && firstAccount.userInfo) {
			console.log('自动登录第一个账户:', firstAccount.userInfo.nickname);

			try {
				// 设置为当前账户
				setAccountCurrent(firstAccount.userInfo.mid);

				// 设置用户信息（但不触发API调用）
				uni.setStorageSync(USER_INFO, firstAccount.userInfo);

				// 只设置token值，不触发后续API调用
				token.value = firstAccount.token;
				return true;
			} catch (error) {
				console.error('自动登录失败:', error);
				return false;
			}
		}

		return false;
	}

	// 安全执行setToken，不会在初始化阶段触发API调用
	function safeSetToken(newToken: string = '') {
		// 只设置token值，不触发API调用
		token.value = newToken;
	}

	// 初始化后完整的登录流程
	function completeLogin() {
		// 确保已有token
		if (!token.value) return;

		try {
			// 读取用户信息
			const userInfo = uni.getStorageSync(USER_INFO);
			if (!userInfo || !userInfo.mid) return;

			// 执行正常的登录后续操作
			projectData().getShareData();
			projectData().fetchConfig('init');
			projectData().fetchUserInfo('init');
			useReferralBindingStore().bindReferral();
		} catch (error) {
			console.error('完成登录过程出错:', error);
		}
	}

	// 初始化
	initAccountsList();

	// 尝试自动登录第一个账户(初始化阶段只设置token，不触发API调用)
	autoLoginFirstAccount();

	return {
		// 状态
		sh,
		bh,
		nh,
		th,
		token,
		token2,
		plat,
		menuData,
		// 计算属性
		showSwitch,
		isLoggedIn,
		// 方法
		getAccountsList,
		saveAccountsList,
		updateCurrentAccount,
		setToken,
		safeSetToken,
		mpInit,
		setSysData,
		switchToAccount,
		switchToNewAccount,
		refreshAccountsList,
		setAccountCurrent,
		autoLoginFirstAccount,
		completeLogin,
		locationCache,
	};
});
